import type { CSSProperties } from 'react';

import type { GeoType } from '../../../../api/regions';

export interface CompDraft {
  geoType: GeoType;
  geoRef: string;
  label: string;
}

interface Props {
  comps: CompDraft[];
  isNew: (c: CompDraft) => boolean;
  keyOf: (c: CompDraft) => string;
  onRemove: (key: string) => void;
}

const chipStyle = (highlight: boolean): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  padding: '5px 8px 5px 10px',
  borderRadius: 8,
  border: `1px solid ${highlight ? 'var(--nb-info)' : 'var(--nb-border)'}`,
  background: 'var(--nb-surface)',
  fontSize: 12.5,
});

/** The working set of composition geographies, as removable chips. */
export function RegionComponentChips({ comps, isNew, keyOf, onRemove }: Props) {
  if (comps.length === 0) {
    return (
      <span style={{ fontSize: 12.5, color: 'var(--nb-subtle)' }}>
        No areas yet — search above to add states, counties, or cities.
      </span>
    );
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {comps.map((c) => {
        const key = keyOf(c);
        const fresh = isNew(c);
        return (
          <span key={key} style={chipStyle(fresh)}>
            <span className="nb-eyebrow" style={{ fontSize: 8.5, color: 'var(--nb-faint)' }}>
              {c.geoType}
            </span>
            <span style={{ color: 'var(--nb-ink)', fontWeight: 600 }}>{c.label}</span>
            {fresh && (
              <span style={{ fontSize: 8.5, fontWeight: 700, color: 'var(--nb-info)' }}>NEW</span>
            )}
            <button
              aria-label={`Remove ${c.label}`}
              onClick={() => onRemove(key)}
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: 'var(--nb-faint)',
              }}
            >
              ✕
            </button>
          </span>
        );
      })}
    </div>
  );
}
